import {
  ComboBox,
  ComboBoxFilterChangeEvent,
  ComboBoxPageChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { FieldRenderProps } from "@progress/kendo-react-form";
import { Hint, Error } from "@progress/kendo-react-labels";
import { filterBy, FilterDescriptor } from "@progress/kendo-data-query";
import { useEffect, useState } from "react";

interface CustomComboBoxProps extends Omit<FieldRenderProps, ""> {
  onFocus?: () => void;
  onBlur?: () => void;
  validationMessage?: string | null;
  touched?: boolean;
  modified?: boolean;
  visited?: boolean;
  valid?: boolean;
  children?: any;
}

const pageSize = 10;

const CustomComboBoxPaginated = (fieldRenderProps: CustomComboBoxProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    max,
    rows,
    value,
    showTextLimitHint,
    loading,
    data,
    textField,
    sorting = false, // if true, sorting enabled.
    sortField, // if 'data' is array of object then send the field name (on the basis of which you want sorting) in 'sortField' prop as string . if 'data' is array of primitive datatype then no need to send any 'sortField' prop.
    sortOrder = "asc", // send 'asc' or 'desc' for ascending or descending sorting respectively.
    filtering = false, // if true, filtering enabled.
    filterFields, // send those field names in the form of array of strings on the basis of which you want filtering. If no 'filterFields' prop is sent then kendo would take 'textField' prop to do the filtering. If no 'textField' and no 'filterFields' props are there, then no filtering will be done.
    ...others
  } = fieldRenderProps;

  const [items, setItems] = useState(data?.slice());
  const [skip, setSkip] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [shownItems, setShownItems] = useState<any>();

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";

  const filterData = (filter: FilterDescriptor) => {
    const newData = data?.slice();
    const filteredData =
      filterFields && filterFields?.length > 0
        ? newData.filter((row: any) =>
            Object.entries(row).some(([key, value]: [string, any]) => {
              if (filterFields?.includes(key) && typeof value === "string") {
                return value.toLowerCase().includes(filter.value.toLowerCase());
              }
              return false;
            })
          )
        : filterBy(newData, filter);
    return filteredData;
  };

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
      setSkip(0);
      setItems(filterData(event.filter));    
  };

  useEffect(()=>{
    setShownItems(items.slice(0, pageSize));
  },[items]);

  useEffect(()=>{
    setShownItems(items.slice(skip, skip + pageSize));
  },[skip]);

  useEffect(() => {
    setItems(data?.slice());
    setTotal(data.length);
    setShownItems(data?.slice(0, pageSize));
  }, [data]);

const pageChange = (event: ComboBoxPageChangeEvent) => {
    setSkip(event.page.skip);
};

  return (
    <div className={"k-form-field-wrap"}>
      <ComboBox
        filterable={filtering}
        onFilterChange={filterChange}
        loading={loading}
        data={shownItems}
        value={value}
        disabled={disabled}
        textField={textField}
        virtual={{
            total: total,
            skip: skip,
            pageSize: pageSize
        }}
        onPageChange={pageChange}
        {...others}
      />
      {showTextLimitHint && (
        <Hint direction={"end"}>
          {value.length} / {max}
        </Hint>
      )}
      {showHint && <Hint id={hindId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </div>
  );
};

export default CustomComboBoxPaginated;
